import { useCallback, useEffect, useRef } from 'react';
import { useSettings } from '@/settings/settings';

/**
 * WebAudio sound cues — off by default, gated by settings.sound.
 * Uses the oscillator API so no audio files are needed and the bundle stays lean.
 * Never the sole channel: every cue has a visual equivalent.
 */
export interface SoundCues {
  /** Short high-pitched chime for a new critical or high insight. */
  alert: () => void;
  /** Soft low pulse while voice recognition is active (call once to start, once to stop). */
  listenStart: () => void;
  listenStop: () => void;
}

export function useSoundCues(): SoundCues {
  const { settings } = useSettings();
  const ctxRef = useRef<AudioContext | null>(null);
  const listenNodeRef = useRef<OscillatorNode | null>(null);

  const ctx = useCallback((): AudioContext | null => {
    if (!settings.sound) return null;
    if (typeof AudioContext === 'undefined') return null;
    if (!ctxRef.current || ctxRef.current.state === 'closed') {
      ctxRef.current = new AudioContext();
    }
    return ctxRef.current;
  }, [settings.sound]);

  const alert = useCallback(() => {
    const ac = ctx();
    if (!ac) return;
    // Two-tone ascending chime: 880 Hz → 1320 Hz, ~180 ms total.
    const now = ac.currentTime;
    const gain = ac.createGain();
    gain.connect(ac.destination);
    gain.gain.setValueAtTime(0.18, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);

    [880, 1320].forEach((freq, i) => {
      const osc = ac.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + i * 0.07);
      osc.connect(gain);
      osc.start(now + i * 0.07);
      osc.stop(now + i * 0.07 + 0.11);
    });
  }, [ctx]);

  const listenStart = useCallback(() => {
    if (listenNodeRef.current) return;
    const ac = ctx();
    if (!ac) return;
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = 'sine';
    osc.frequency.value = 440;
    gain.gain.value = 0.06;
    osc.connect(gain);
    gain.connect(ac.destination);
    osc.start();
    listenNodeRef.current = osc;
  }, [ctx]);

  const listenStop = useCallback(() => {
    if (!listenNodeRef.current) return;
    try {
      listenNodeRef.current.stop();
    } catch {
      /* already stopped */
    }
    listenNodeRef.current = null;
  }, []);

  // Stop the listen tone if sound is turned off mid-session.
  useEffect(() => {
    if (!settings.sound) listenStop();
  }, [settings.sound, listenStop]);

  return { alert, listenStart, listenStop };
}
